import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Clock, Plus, ReceiptEuro, User, CreditCard, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { money, formatDateTime } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { DocumentsTab } from "@/components/order/DocumentsTab";
import { MandateTab } from "@/components/order/MandateTab";
import { FamilyAuthTab } from "@/components/order/FamilyAuthTab";
import { PodTab } from "@/components/order/PodTab";
import { LogisticsTab } from "@/components/order/LogisticsTab";
import { ChatTab } from "@/components/order/ChatTab";
import { TasksTab } from "@/components/order/TasksTab";
import { PartnerPayoutPanel } from "@/components/order/PartnerPayoutPanel";
import { MultiPartnerAssign } from "@/components/order/MultiPartnerAssign";
import { PaymentTab } from "@/components/order/PaymentTab";
import { MarginPanel } from "@/components/order/MarginPanel";
import { AdjustmentsPanel } from "@/components/order/AdjustmentsPanel";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

const STATUSES = ["pending", "confirmed", "in_transit", "delivered", "cancelled"];
const TRACKING_EVENTS = ["confirmed", "in_transit", "delayed", "incident", "delivered"];

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [partners, setPartners] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [trk, setTrk] = useState({ status: "in_transit", note: "", location: "" });
  const [trkOpen, setTrkOpen] = useState(false);
  const [paying, setPaying] = useState(false);
  const [margin, setMargin] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const isStaff = ["admin", "support"].includes(user?.role);
  const canSeePayment = !["driver", "partner"].includes(user?.role);
  const canTrack = ["admin", "support", "partner", "driver"].includes(user?.role);

  const load = async () => {
    try { const { data } = await api.get(`/orders/${id}`); setOrder(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reloadMargin = () => {
    if (["admin", "support"].includes(user?.role)) {
      api.get(`/orders/${id}/margin`).then(({ data }) => setMargin(data)).catch(() => {});
    }
  };
  const onOrderChange = (o) => { if (o) setOrder(o); reloadMargin(); };
  useEffect(() => {
    load();
    if (isStaff) {
      reloadMargin();
      api.get("/partners").then(({ data }) => setPartners(data)).catch(() => {});
      api.get("/drivers").then(({ data }) => setDrivers(data)).catch(() => {});
      api.get("/vehicles").then(({ data }) => setVehicles(data)).catch(() => {});
    } else if (user?.role === "partner") {
      api.get("/drivers").then(({ data }) => setDrivers(data)).catch(() => {});
      api.get("/vehicles").then(({ data }) => setVehicles(data)).catch(() => {});
    }
  }, [id]);

  const respondJob = async (action) => {
    try { const { data } = await api.post(`/orders/${id}/partner-response`, { action }); setOrder(data); toast.success(action === "accept" ? t("od.jobAccepted") : t("od.jobRefused")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const updateStatus = async (status) => {
    try { const { data } = await api.patch(`/orders/${id}/status`, { status }); setOrder(data); toast.success(t("od.statusUpdated")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const assign = async (field, value) => {
    try { const { data } = await api.patch(`/orders/${id}/assign`, { [field]: value }); onOrderChange(data); toast.success(t("od.assigned")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const assignDriverPartner = async (driver_id) => {
    try { const { data } = await api.patch(`/orders/${id}/assign-driver`, { driver_id }); setOrder(data); toast.success(t("od.driverAssigned")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const assignVehiclePartner = async (vehicle_id) => {
    try { const { data } = await api.patch(`/orders/${id}/assign-vehicle`, { vehicle_id }); setOrder(data); toast.success(t("od.assigned")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addTracking = async () => {
    try { const { data } = await api.post(`/orders/${id}/tracking`, trk); setOrder(data); setTrkOpen(false); setTrk({ status: "in_transit", note: "", location: "" }); toast.success(t("od.trackingAdded")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const createInvoice = async () => {
    try {
      const { data } = await api.post("/invoices", {
        order_id: id, client_id: order.client_id, client_name: order.client_name,
        client_email: order.client_email,
        lines: [{ description: `Servizio ordine ${order.number} (${order.origin} → ${order.destination})`, quantity: 1, unit_price: order.pricing?.subtotal || order.total || 0 }],
      });
      toast.success(`${t("od.invoiceCreated")} · ${data.number}`);
      navigate(`/invoices/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const payOrder = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/orders/${id}/pay`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setPaying(false); }
  };

  if (!order) return <div className="text-slate-400 py-10">{t("common.loading")}</div>;
  const tab = searchParams.get("tab") || "overview";
  const setTab = (v) => setSearchParams((p) => { p.set("tab", v); return p; }, { replace: true });

  return (
    <div className="space-y-6 max-w-5xl" data-testid="order-detail-page">
      <button onClick={() => navigate("/orders")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft size={16} /> {t("common.back")}</button>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-display text-2xl lg:text-3xl font-bold text-slate-900">{order.number}</h1>
            <StatusBadge status={order.status} />
            {order.partner_acceptance && (
              <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium border ${order.partner_acceptance === "accepted" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : order.partner_acceptance === "refused" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200"}`} data-testid="partner-acceptance-badge">
                {order.partner_acceptance === "accepted" ? t("od.pAccepted") : order.partner_acceptance === "refused" ? t("od.pRefused") : t("od.pWaiting")}
              </span>
            )}
            {order.payment_status === "paid" && (
              <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid="order-paid-badge">
                <CheckCircle2 size={13} /> {t("order.paid")}
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500 mt-1 font-mono">{t("od.trackingLabel")}: {order.tracking_code}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isStaff && (
            <Select value={order.status} onValueChange={updateStatus}>
              <SelectTrigger className="w-44" data-testid="order-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {isStaff && <Button onClick={createInvoice} className="bg-blue-900 hover:bg-blue-800" data-testid="order-create-invoice-btn"><ReceiptEuro size={16} className="mr-1.5" /> {t("od.crearFattura")}</Button>}
          {user?.role === "client" && order.payment_status !== "paid" && order.status !== "pending" && order.status !== "cancelled" && order.total > 0 && (
            <Button onClick={payOrder} disabled={paying} className="bg-emerald-600 hover:bg-emerald-700" data-testid="order-pay-btn">
              <CreditCard size={16} className="mr-1.5" /> {paying ? "..." : `${t("order.payNow")} · ${money(order.total, order.currency)}`}
            </Button>
          )}
          {user?.role === "partner" && order.partner_acceptance === "waiting" && (
            <>
              <Button onClick={() => respondJob("accept")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="partner-accept-btn"><CheckCircle2 size={16} className="mr-1.5" /> {t("od.accept")}</Button>
              <Button onClick={() => respondJob("refuse")} variant="outline" className="text-rose-600" data-testid="partner-refuse-btn">{t("od.refuse")}</Button>
            </>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList data-testid="order-tabs" className="max-w-full justify-start overflow-x-auto">
          <TabsTrigger value="overview" data-testid="tab-overview">{t("od.tabOverview")}</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">{t("od.tabDocs")}</TabsTrigger>
          <TabsTrigger value="mandate" data-testid="tab-mandate">{t("od.tabMandate")}</TabsTrigger>
          <TabsTrigger value="family" data-testid="tab-family">{t("od.tabFamily")}</TabsTrigger>
          <TabsTrigger value="pod" data-testid="tab-pod">{t("od.tabPod")}</TabsTrigger>
          <TabsTrigger value="logistics" data-testid="tab-logistics">{t("od.tabLogistics")}</TabsTrigger>
          {canSeePayment && <TabsTrigger value="payment" data-testid="tab-payment">{t("od.tabPayment")}</TabsTrigger>}
          <TabsTrigger value="chat" data-testid="tab-chat">{t("od.tabChat")}</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks">{t("od.tabTasks")}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <h3 className="font-display text-lg font-semibold mb-4">{t("od.details")}</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <Info label={t("order.fClient")} value={order.client_name} />
              <Info label={t("od.deceased")} value={order.deceased_name} />
              <Info label={t("order.fOrigin")} value={order.origin} />
              <Info label={t("order.fDest")} value={order.destination} />
              <Info label={t("order.fService")} value={order.service_code} />
              <Info label={t("order.fTransport")} value={order.transport_code || order.transport_type_code} />
              <Info label={t("od.distOp")} value={`${order.billable_km || order.distance_km || 0} km`} />
              {order.total != null && <Info label={t("common.total")} value={money(order.total, order.currency)} />}
            </div>
          </div>

          {isStaff && <AdjustmentsPanel order={order} onUpdated={onOrderChange} />}

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-semibold">{t("od.tracciamento")}</h3>
              {canTrack && (
                <Dialog open={trkOpen} onOpenChange={setTrkOpen}>
                  <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="add-tracking-btn"><Plus size={14} className="mr-1" /> {t("od.event")}</Button></DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle className="font-display">{t("od.addEvent")}</DialogTitle></DialogHeader>
                    <div className="space-y-3 py-2">
                      <div className="space-y-1.5"><Label>{t("common.status")}</Label>
                        <Select value={trk.status} onValueChange={(v) => setTrk({ ...trk, status: v })}>
                          <SelectTrigger data-testid="tracking-status"><SelectValue /></SelectTrigger>
                          <SelectContent>{TRACKING_EVENTS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                        </Select></div>
                      <div className="space-y-1.5"><Label>{t("od.location")}</Label><Input value={trk.location} onChange={(e) => setTrk({ ...trk, location: e.target.value })} data-testid="tracking-location" /></div>
                      <div className="space-y-1.5"><Label>{t("od.note")}</Label><Textarea value={trk.note} onChange={(e) => setTrk({ ...trk, note: e.target.value })} data-testid="tracking-note" /></div>
                    </div>
                    <DialogFooter><Button onClick={addTracking} className="bg-blue-900 hover:bg-blue-800" data-testid="tracking-save-btn">{t("common.save")}</Button></DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </div>
            <div className="space-y-0">
              {(order.timeline || []).slice().reverse().map((ev, i) => (
                <div key={ev.timestamp || `tl-${i}`} className="flex gap-4 pb-6 relative" data-testid="timeline-event">
                  <div className="flex flex-col items-center">
                    <div className="h-3 w-3 rounded-full bg-blue-900 mt-1.5" />
                    {i < order.timeline.length - 1 && <div className="w-px flex-1 bg-slate-200 mt-1" />}
                  </div>
                  <div className="flex-1 -mt-0.5">
                    <div className="flex items-center gap-2"><StatusBadge status={ev.status} /><span className="text-xs text-slate-400 flex items-center gap-1"><Clock size={12} /> {formatDateTime(ev.timestamp)}</span></div>
                    {ev.note && <p className="text-sm text-slate-600 mt-1">{ev.note}</p>}
                    {ev.location && <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1"><MapPin size={12} /> {ev.location}</p>}
                    {ev.by && <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1"><User size={12} /> {ev.by}{ev.by_role ? ` · ${ev.by_role}` : ""}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {isStaff && <MarginPanel margin={margin} />}
        {isStaff && (
          <MultiPartnerAssign order={order} partners={partners} onSaved={onOrderChange} />
        )}
        {isStaff && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4 h-fit">
            <h3 className="font-display text-lg font-semibold">{t("od.assegnazione")}</h3>
            <div className="space-y-1.5"><Label>{t("od.driver")}</Label>
              <Select value={order.driver_id || ""} onValueChange={(v) => assign("driver_id", v)}>
                <SelectTrigger data-testid="assign-driver"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{drivers.filter((d) => !order.partner_id || d.partner_id === order.partner_id).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>{t("od.vehicle")}</Label>
              <Select value={order.vehicle_id || ""} onValueChange={(v) => assign("vehicle_id", v)}>
                <SelectTrigger data-testid="assign-vehicle"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{vehicles.filter((v) => !order.partner_id || v.partner_id === order.partner_id).map((v) => <SelectItem key={v.id} value={v.id}>{v.plate}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
        )}
        {user?.role === "partner" && Array.isArray(order.partner_assignments) && order.partner_assignments.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-3 h-fit" data-testid="partner-assigned-amount-card">
            <h3 className="font-display text-lg font-semibold">{t("od.yourPay")}</h3>
            {order.partner_assignments.map((a, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2.5">
                <span className="text-sm text-slate-600">{a.note || t("od.assignedAmount")}</span>
                <span className="font-bold text-emerald-700" data-testid="partner-assigned-amount">{money(a.amount, order.currency)}</span>
              </div>
            ))}
          </div>
        )}
        {user?.role === "partner" && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4 h-fit" data-testid="partner-assign-driver-card">
            <h3 className="font-display text-lg font-semibold">{t("od.assignDriver")}</h3>
            <p className="text-xs text-slate-500">{t("od.chooseDriver")}</p>
            <div className="space-y-1.5"><Label>{t("od.driver")}</Label>
              <Select value={order.driver_id || ""} onValueChange={assignDriverPartner}>
                <SelectTrigger data-testid="partner-assign-driver"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select></div>
            {drivers.length === 0 && <p className="text-xs text-amber-600" data-testid="partner-no-drivers">{t("od.noDrivers")}</p>}
            <div className="space-y-1.5"><Label>{t("od.vehicle")}</Label>
              <Select value={order.vehicle_id || ""} onValueChange={assignVehiclePartner}>
                <SelectTrigger data-testid="partner-assign-vehicle"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.plate}</SelectItem>)}</SelectContent>
              </Select></div>
            {vehicles.length === 0 && <p className="text-xs text-amber-600" data-testid="partner-no-vehicles">{t("od.noVehicles") !== "od.noVehicles" ? t("od.noVehicles") : "Nessun veicolo. Aggiungine uno nella pagina Veicoli."}</p>}
          </div>
        )}
        {(isStaff || user?.role === "partner") && (
          <PartnerPayoutPanel order={order} onUpdated={onOrderChange} />
        )}
          </div>
        </TabsContent>

        <TabsContent value="documents" className="mt-6">
          <DocumentsTab orderId={id} />
        </TabsContent>
        <TabsContent value="mandate" className="mt-6">
          <MandateTab order={order} onUpdated={setOrder} />
        </TabsContent>
        <TabsContent value="family" className="mt-6">
          <FamilyAuthTab order={order} onUpdated={setOrder} />
        </TabsContent>
        <TabsContent value="pod" className="mt-6">
          <PodTab order={order} onUpdated={setOrder} />
        </TabsContent>
        <TabsContent value="logistics" className="mt-6">
          <LogisticsTab order={order} onUpdated={setOrder} />
        </TabsContent>
        <TabsContent value="payment" className="mt-6">
          {canSeePayment && <PaymentTab order={order} onUpdated={onOrderChange} />}
        </TabsContent>
        <TabsContent value="chat" className="mt-6">
          <ChatTab order={order} />
        </TabsContent>
        <TabsContent value="tasks" className="mt-6">
          <TasksTab order={order} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-slate-800 font-medium mt-0.5 capitalize">{value || "—"}</div>
    </div>
  );
}

